import React, {useState, useEffect, useRef, useCallback} from 'react';
import {View, Text, StyleSheet, TouchableOpacity, Modal, Animated, Alert, TextInput} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Colors} from '../theme/colors';
import {Header} from '../components/Header';
import {useAuth} from '../hooks/useAuth';
import {useTheme} from '../hooks/useTheme';
import {Icon} from '../components/Icon';
import {
  NotificationMode,
  getNotificationPreference,
  setNotificationPreference,
} from '../services/notificationPreferences';
import {notificationService} from '../services/NotificationService';
import {Linking} from 'react-native';

type RootStackParamList = {
  Login: undefined;
};

export const ProfileScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const {user, isAuthenticated, logout, deleteAccount, requestAccountDeletion} = useAuth();
  const {colors, themePreference, setThemePreference} = useTheme();
  const [notificationMode, setNotificationMode] = useState<NotificationMode>('all');
  const [showNotificationPicker, setShowNotificationPicker] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const themeScaleAnim = useRef(new Animated.Value(1)).current;
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteCodeModal, setShowDeleteCodeModal] = useState(false);
  const [deleteCode, setDeleteCode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const handleThemeSelect = useCallback((pref: 'light' | 'dark' | 'system') => {
    setThemePreference(pref);
    // Brief scale pulse animation
    Animated.sequence([
      Animated.timing(themeScaleAnim, {
        toValue: 1.05,
        duration: 120,
        useNativeDriver: true,
      }),
      Animated.timing(themeScaleAnim, {
        toValue: 1,
        duration: 120,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShowThemePicker(false);
    });
  }, [setThemePreference, themeScaleAnim]);

  useEffect(() => {
    loadNotificationPreference();
  }, []);

  const loadNotificationPreference = async () => {
    const mode = await getNotificationPreference();
    setNotificationMode(mode);
  };

  const handleNotificationModeChange = async (mode: NotificationMode) => {
    await setNotificationPreference(mode);
    setNotificationMode(mode);
    setShowNotificationPicker(false);
    // Re-register with backend to update preference
    await notificationService.registerWithBackend();
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDeletionRequest = async () => {
    setRequestLoading(true);
    const result = await requestAccountDeletion();
    setRequestLoading(false);
    if (result.success) {
      setShowDeleteConfirmModal(false);
      setDeleteCode('');
      setShowDeleteCodeModal(true);
    } else {
      Alert.alert('Errore', result.message || 'Errore durante la richiesta.');
    }
  };

  const handleConfirmDeleteCode = async () => {
    if (!deleteCode.trim()) {
      Alert.alert('Errore', 'Inserisci il codice di verifica.');
      return;
    }
    setDeleteLoading(true);
    const result = await deleteAccount(deleteCode.trim());
    setDeleteLoading(false);
    if (result.success) {
      setShowDeleteCodeModal(false);
      Alert.alert('Account Eliminato', 'Il tuo account e\' stato eliminato con successo.');
    } else {
      Alert.alert('Errore', result.message || 'Codice non valido.');
    }
  };

  const getNotificationModeLabel = () => {
    return notificationMode === 'all' ? 'Tutte le notizie' : 'Solo importanti';
  };

  const getThemeLabel = () => {
    if (themePreference === 'system') return 'Sistema';
    if (themePreference === 'dark') return 'Scuro';
    return 'Chiaro';
  };

  return (
    <View style={styles.container}>
      <Header />
      <View style={[styles.content, {backgroundColor: colors.background}]}>
        <View style={styles.section}>
            {isAuthenticated && user ? (
            <View style={styles.userInfo}>
                {/* User Avatar */}
                <View style={styles.avatarContainer}>
                <View style={styles.avatar}>
                    <Text style={styles.avatarText}>{getInitials(user.name)}</Text>
                </View>
                </View>
                
                <Text style={[styles.userName, {color: colors.textPrimary}]}>{user.name}</Text>
                <Text style={[styles.userEmail, {color: colors.textSecondary}]}>{user.email}</Text>
                
                <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
                <Text style={styles.logoutText}>Esci</Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.deleteAccountButton} onPress={handleDeleteAccount}>
                <Text style={styles.deleteAccountText}>Elimina Account</Text>
                </TouchableOpacity>
            </View>
            ) : (
            <View style={styles.userInfo}>
                <Text style={[styles.icon, {color: colors.textPrimary}]}>👤</Text>
                <Text style={[styles.title, {color: colors.textPrimary}]}>Profilo</Text>
                <Text style={[styles.subtitle, {color: colors.textSecondary}]}>Accedi per vedere il tuo profilo</Text>
                
                <TouchableOpacity 
                style={styles.loginButton}
                onPress={() => navigation.navigate('Login')}
                >
                <Text style={styles.loginText}>ACCEDI</Text>
                </TouchableOpacity>
            </View>
            )}
        </View>

        {/* Settings Section */}
        <View style={styles.section}>
            <Text style={[styles.sectionTitle, {color: colors.textSecondary}]}>IMPOSTAZIONI</Text>
            
            {/* Notification Settings */}
            <TouchableOpacity 
              style={[styles.settingRow, {borderBottomColor: colors.border}]}
              onPress={() => setShowNotificationPicker(true)}
              activeOpacity={0.7}
            >
                <View style={styles.settingContent}>
                    <Text style={[styles.settingLabel, {color: colors.textPrimary}]}>Notifiche</Text>
                    <Text style={[styles.settingDescription, {color: colors.textSecondary}]}>
                        {getNotificationModeLabel()}
                    </Text>
                </View>
                <Text style={[styles.chevron, {color: colors.textSecondary}]}>›</Text>
            </TouchableOpacity>
            
            {/* Theme Settings */}
            <TouchableOpacity 
              style={[styles.settingRow, {borderBottomColor: colors.border}]}
              onPress={() => setShowThemePicker(true)}
              activeOpacity={0.7}
            >
                <View style={styles.settingContent}>
                    <Text style={[styles.settingLabel, {color: colors.textPrimary}]}>Tema</Text>
                    <Text style={[styles.settingDescription, {color: colors.textSecondary}]}>
                        {getThemeLabel()}
                    </Text>
                </View>
                <Text style={[styles.chevron, {color: colors.textSecondary}]}>›</Text>
            </TouchableOpacity>
        </View>

        {/* Informazioni Section */}
        <View style={styles.section}>
            <Text style={[styles.sectionTitle, {color: colors.textSecondary}]}>INFORMAZIONI</Text>
            
            {/* Contattaci */}
            <TouchableOpacity 
              style={[styles.settingRow, {borderBottomColor: colors.border}]}
              onPress={async () => {
                const url = 'mailto:redazione@laviola.it';
                try {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Contattaci', 'Nessun client email trovato. Scrivici a:\n\nredazione@laviola.it');
                  }
                } catch (_e) {
                  Alert.alert('Contattaci', 'Scrivici a:\n\nredazione@laviola.it');
                }
              }}
              activeOpacity={0.7}
            >
                <View style={styles.settingContent}>
                    <Text style={[styles.settingLabel, {color: colors.textPrimary}]}>Contattaci / Contact Us</Text>
                    <Text style={[styles.settingDescription, {color: colors.textSecondary}]}>
                        redazione@laviola.it
                    </Text>
                </View>
                <Text style={[styles.chevron, {color: colors.textSecondary}]}>›</Text>
            </TouchableOpacity>
        </View>
      </View>

      {/* Notification Mode Picker Modal */}
      <Modal
        visible={showNotificationPicker}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowNotificationPicker(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowNotificationPicker(false)}
        >
          <View style={[styles.modalContent, {backgroundColor: colors.surface}]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, {color: colors.textPrimary}]}>Preferenze Notifiche</Text>
              <TouchableOpacity onPress={() => setShowNotificationPicker(false)} hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
                <Icon name="xmark" size={18} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            
            <TouchableOpacity
              style={[
                styles.modalOption,
                notificationMode === 'all' && styles.modalOptionSelected,
              ]}
              onPress={() => handleNotificationModeChange('all')}
            >
              <Text style={[styles.modalOptionText, {color: colors.textPrimary}]}>
                Tutte le notizie
              </Text>
              {notificationMode === 'all' && <Text style={styles.checkmark}>✓</Text>}
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.modalOption,
                notificationMode === 'important' && styles.modalOptionSelected,
              ]}
              onPress={() => handleNotificationModeChange('important')}
            >
              <Text style={[styles.modalOptionText, {color: colors.textPrimary}]}>
                Solo le più importanti
              </Text>
              {notificationMode === 'important' && <Text style={styles.checkmark}>✓</Text>}
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Theme Picker Modal */}
      <Modal
        visible={showThemePicker}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowThemePicker(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowThemePicker(false)}
        >
          <View style={[styles.modalContent, {backgroundColor: colors.surface}]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, {color: colors.textPrimary}]}>Tema</Text>
              <TouchableOpacity onPress={() => setShowThemePicker(false)} hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
                <Icon name="xmark" size={18} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            
            {/* Chiaro */}
            <Animated.View style={{transform: [{scale: themePreference === 'light' ? themeScaleAnim : 1}]}}>
            <TouchableOpacity
              style={[
                styles.themeOptionRow,
                themePreference === 'light' && {backgroundColor: 'rgba(255, 252, 240, 0.6)'},
              ]}
              onPress={() => handleThemeSelect('light')}
              activeOpacity={0.7}
            >
              <View style={[
                styles.themeIconCircle,
                {backgroundColor: '#FFF8E1', borderColor: themePreference === 'light' ? colors.primary : colors.border},
                themePreference === 'light' && styles.themeIconCircleSelected,
              ]}>
                <Icon name="sun" size={20} color="#FF9800" />
              </View>
              <Text style={[
                styles.themeLabel,
                {color: colors.textPrimary},
                themePreference === 'light' && {fontWeight: '700'},
              ]}>Chiaro</Text>
            </TouchableOpacity>
            </Animated.View>

            {/* Scuro */}
            <Animated.View style={{transform: [{scale: themePreference === 'dark' ? themeScaleAnim : 1}]}}>
            <TouchableOpacity
              style={[
                styles.themeOptionRow,
                themePreference === 'dark' && {backgroundColor: 'rgba(100, 140, 230, 0.2)'},
              ]}
              onPress={() => handleThemeSelect('dark')}
              activeOpacity={0.7}
            >
              <View style={[
                styles.themeIconCircle,
                {backgroundColor: '#1A1A2E', borderColor: themePreference === 'dark' ? colors.primary : colors.border},
                themePreference === 'dark' && styles.themeIconCircleSelected,
              ]}>
                <View style={{shadowColor: '#D4DFFF', shadowOffset: {width: 0, height: 0}, shadowOpacity: 0.8, shadowRadius: 8}}>
                  <Icon name="moon" size={18} color="#D4DFFF" />
                </View>
              </View>
              <Text style={[
                styles.themeLabel,
                {color: colors.textPrimary},
                themePreference === 'dark' && {fontWeight: '700'},
              ]}>Scuro</Text>
            </TouchableOpacity>
            </Animated.View>

            {/* Sistema */}
            <Animated.View style={{transform: [{scale: themePreference === 'system' ? themeScaleAnim : 1}]}}>
            <TouchableOpacity
              style={[
                styles.themeOptionRow,
                themePreference === 'system' && {backgroundColor: 'rgba(243, 229, 245, 0.4)'},
              ]}
              onPress={() => handleThemeSelect('system')}
              activeOpacity={0.7}
            >
              <View style={[
                styles.themeIconCircle,
                {backgroundColor: colors.surfaceVariant, borderColor: themePreference === 'system' ? colors.primary : colors.border},
                themePreference === 'system' && styles.themeIconCircleSelected,
              ]}>
                <Icon name="globe" size={18} color={colors.primary} />
              </View>
              <Text style={[
                styles.themeLabel,
                {color: colors.textPrimary},
                themePreference === 'system' && {fontWeight: '700'},
              ]}>Sistema</Text>
            </TouchableOpacity>
            </Animated.View>
          </View>
        </TouchableOpacity>
      </Modal>
      {/* Delete Account Confirmation Modal */}
      <Modal visible={showDeleteConfirmModal} transparent animationType="fade">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowDeleteConfirmModal(false)}>
          <View
            style={[styles.modalContent, {backgroundColor: colors.surface}]}
            onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, {color: colors.textPrimary}]}>
                Elimina Account
              </Text>
              <TouchableOpacity onPress={() => setShowDeleteConfirmModal(false)}>
                <Icon name="xmark" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <Text style={[styles.deleteCodeDescription, {color: colors.textSecondary}]}>
              Sei sicuro di voler eliminare il tuo account? Questa azione e' irreversibile e tutti i tuoi dati verranno cancellati permanentemente.
            </Text>
            <TouchableOpacity
              style={[styles.deleteCodeConfirmButton, requestLoading && {opacity: 0.6}]}
              onPress={handleConfirmDeletionRequest}
              disabled={requestLoading}>
              <Text style={styles.deleteCodeConfirmText}>
                {requestLoading ? 'Invio codice...' : 'Continua'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.deleteCodeCancelButton}
              onPress={() => setShowDeleteConfirmModal(false)}>
              <Text style={[styles.deleteCodeCancelText, {color: colors.textSecondary}]}>
                Annulla
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Delete Account Code Verification Modal */}
      <Modal visible={showDeleteCodeModal} transparent animationType="fade">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowDeleteCodeModal(false)}>
          <View
            style={[styles.modalContent, {backgroundColor: colors.surface}]}
            onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, {color: colors.textPrimary}]}>
                Codice di Verifica
              </Text>
              <TouchableOpacity onPress={() => setShowDeleteCodeModal(false)}>
                <Icon name="xmark" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <Text style={[styles.deleteCodeDescription, {color: colors.textSecondary}]}>
              Abbiamo inviato un codice di verifica alla tua email. Inseriscilo qui sotto per confermare l'eliminazione del tuo account.
            </Text>
            <TextInput
              style={[styles.deleteCodeInput, {color: colors.textPrimary, borderColor: colors.border}]}
              value={deleteCode}
              onChangeText={setDeleteCode}
              placeholder="000000"
              placeholderTextColor={colors.textSecondary}
              keyboardType="number-pad"
              maxLength={6}
              autoFocus
            />
            <TouchableOpacity
              style={[styles.deleteCodeConfirmButton, deleteLoading && {opacity: 0.6}]}
              onPress={handleConfirmDeleteCode}
              disabled={deleteLoading}>
              <Text style={styles.deleteCodeConfirmText}>
                {deleteLoading ? 'Eliminazione...' : 'Elimina Account'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.deleteCodeCancelButton}
              onPress={() => setShowDeleteCodeModal(false)}>
              <Text style={[styles.deleteCodeCancelText, {color: colors.textSecondary}]}>
                Annulla
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  section: {
    marginBottom: 40,
    width: '100%',
  },
  userInfo: {
    alignItems: 'center',
    width: '100%',
  },
  icon: {
    fontSize: 60,
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 16,
    textTransform: 'uppercase',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  settingContent: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 13,
  },
  chevron: {
    fontSize: 24,
    fontWeight: '300',
    marginLeft: 12,
  },
  avatarContainer: {
    marginBottom: 16,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: Colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: Colors.textOnPrimary,
    fontSize: 36,
    fontWeight: '700',
  },
  userName: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    marginBottom: 32,
  },
  loginButton: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 40,
    paddingVertical: 14,
    borderRadius: 25,
    width: '80%',
    alignItems: 'center',
  },
  loginText: {
    color: Colors.textOnPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
  logoutButton: {
    borderWidth: 1,
    borderColor: Colors.error,
    paddingHorizontal: 40,
    paddingVertical: 12,
    borderRadius: 25,
    width: '80%',
    alignItems: 'center',
  },
  logoutText: {
    color: Colors.error,
    fontSize: 14,
    fontWeight: '600',
  },
  deleteAccountButton: {
    marginTop: 12,
    paddingHorizontal: 40,
    paddingVertical: 12,
    borderRadius: 25,
    width: '80%',
    alignItems: 'center' as const,
    backgroundColor: Colors.error,
  },
  deleteAccountText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600' as const,
  },
  deleteCodeDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  deleteCodeInput: {
    borderWidth: 1,
    borderRadius: 12,
    fontSize: 24,
    fontWeight: '700' as const,
    textAlign: 'center' as const,
    paddingVertical: 14,
    letterSpacing: 8,
    marginBottom: 16,
  },
  deleteCodeConfirmButton: {
    backgroundColor: Colors.error,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center' as const,
    marginBottom: 8,
  },
  deleteCodeConfirmText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600' as const,
  },
  deleteCodeCancelButton: {
    paddingVertical: 12,
    alignItems: 'center' as const,
  },
  deleteCodeCancelText: {
    fontSize: 14,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 16,
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  modalOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 10,
    marginBottom: 8,
  },
  modalOptionSelected: {
    backgroundColor: Colors.primary + '20',
  },
  modalOptionText: {
    fontSize: 16,
  },
  checkmark: {
    fontSize: 18,
    color: Colors.primary,
    fontWeight: '600',
  },
  modalNote: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  },
  // Theme picker styles
  themeOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    marginBottom: 8,
  },
  themeIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    marginRight: 14,
  },
  themeIconCircleSelected: {
    borderWidth: 2.5,
  },
  themeLabel: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
});
